import { FC, useState, useCallback } from 'react'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import useFormProcessor from '@hooks/useFormProcessor'
import {
  useLazyGetFinalTestTemplateXlsQuery,
  useUploadFinalTestXlsMutation,
} from '@services/courses'
import { File } from '@models/file'
import AddNewItemModal from '@templates/AddNewItemModal'
import { fileField, numberField } from '@templates/AddNewItemModal/templates'
import { parseFormHoursToMinutes } from '@utils/themeHoursTime'
import MaskedInput from '@organisms/MaskedInput'
import { DownloadOutlined } from '@ant-design/icons'
import {
  InfoBlock,
  InfoText,
  InstructionList,
  TemplateDownloadButton,
  XlsFinalTestModalFrame,
} from './styled'

const SUCCESS_TITLE = 'Загружаем итоговое тестирование'
const SUCCESS_DESCRIPTION =
  'После обработки файла вам придёт уведомление на платформу. Если в файле будут ошибки, вы получите их список. После исправления ошибок вам нужно повторно загрузить файл.'

type FormValues = {
  file: File
  passPercentage?: string
  questionsCount?: string
  hoursTime?: string
}

type Props = {
  courseId: number
  handleCloseModal: () => void
}

const fields = [
  {
    fields: [
      fileField({
        name: 'file',
        label: 'Загрузить (xls, xlsx)',
        isRequired: true,
        id: 'xls-final-test-file',
        formats: ['xls', 'xlsx'],
        maxSizeMegaBytes: 10,
        componentProps: {
          formatErrorMessage: 'Допустимые форматы: xls, xlsx',
          sizeErrorMessage: 'Файл больше 10Мб',
          uploadLabel: 'Загрузить файл',
        },
      }),
      {
        name: 'passPercentage',
        label: 'Процент для прохождения*',
        Component: MaskedInput,
        rules: {
          required: { value: true, message: 'Заполните процент' },
          validate: (value: string) => {
            const num = Number(value)
            if (Number.isNaN(num) || num < 0 || num > 100) {
              return 'Процент должен быть от 0 до 100'
            }
            return true
          },
        },
        componentProps: {
          maskOptions: { mask: Number, min: 0, max: 100, scale: 0, signed: false },
          placeholder: 'Процент для прохождения',
        },
      },
      numberField({
        name: 'questionsCount',
        label: 'Количество вопросов для вывода',
        isRequired: true,
        placeholder: 'Количество вопросов для вывода',
        componentProps: {
          maskOptions: { min: 1, scale: 0 },
        },
      }),
      numberField({
        name: 'hoursTime',
        label: 'Количество часов',
        isRequired: true,
        placeholder: 'Количество часов',
        componentProps: {
          maskOptions: { scale: 0 },
        },
      }),
    ],
  },
]

const XlsFinalTestModal: FC<Props> = ({
  courseId,
  handleCloseModal,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<
    FetchBaseQueryError | SerializedError | undefined
  >(undefined)

  const [uploadFinalTestXls, uploadData] = useUploadFinalTestXlsMutation()

  const methods = useFormProcessor({
    apiErrors: submitError,
  })
  const [getFinalTestTemplate, { isLoading: getFinalTestTemplateIsLoading }] =
    useLazyGetFinalTestTemplateXlsQuery()

  const handleDownloadTemplate = () => {
    void getFinalTestTemplate(courseId)
  }

  const handleSubmitForm = useCallback(
    async (form: FormValues) => {
      const fileIri = form.file?.['@id']
      if (!fileIri) return

      const passPercentage = form.passPercentage ? Number(form.passPercentage) : undefined
      const questionsCount = form.questionsCount ? Number(form.questionsCount) : undefined
      const hoursTime = parseFormHoursToMinutes(form.hoursTime)

      if (
        passPercentage === undefined ||
        questionsCount === undefined ||
        hoursTime === undefined
      ) {
        return
      }

      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        await uploadFinalTestXls({
          id: courseId,
          file: fileIri,
          passPercentage,
          questionsCount,
          hoursTime,
        }).unwrap()
      } catch (error) {
        setSubmitError(error as FetchBaseQueryError | SerializedError)
      } finally {
        setIsSubmitting(false)
      }
    },
    [courseId, uploadFinalTestXls]
  )

  return (
    <XlsFinalTestModalFrame>
      <AddNewItemModal
        {...uploadData}
        isLoading={isSubmitting || uploadData.isLoading}
        handleCloseModal={handleCloseModal}
        onSubmitForm={handleSubmitForm}
        methods={methods}
        fields={fields}
        className="xls-final-test-modal"
        customFullTitle="Загрузить итоговое тестирование"
        successContentAddTitle={SUCCESS_TITLE}
        successContentDescription={SUCCESS_DESCRIPTION}
        contentBeforeFields={
          <InfoBlock>
            <InfoText>Скачайте и заполните шаблон загрузки теста</InfoText>
            <TemplateDownloadButton
              type="button"
              autoWidth
              leftIcon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              isLoading={getFinalTestTemplateIsLoading}
            >
              Скачать образец
            </TemplateDownloadButton>
            <InstructionList>
              <li>Вы можете добавить любое количество вопросов и до 10 ответов на каждый из них.</li>
              <li>Вы можете в вопросе выбрать один правильный ответ или несколько.</li>
              <li>Чтобы указать верный ответ, добавьте в столбце &quot;Ответ верный&quot; любое слово или символ.</li>
              <li>Сотрудник получит набор вопросов в случайном порядке в указанном вами количестве.</li>
              <li>Ответы будут выводиться в случайном порядке.</li>
              <li>Тестирование считается пройденным, если сотрудник набрал указанный вами процент или выше.</li>
              <li>После загрузки вы сможете отредактировать итоговое тестирование.</li>
            </InstructionList>
          </InfoBlock>
        }
      />
    </XlsFinalTestModalFrame>
  )
}

export default XlsFinalTestModal
